/**
 * Terrain lighting/hillshade wiring for the Giro3D engine.
 *
 * Giro3D's `Map` entity supports `MapLightingMode` for GPU-based
 * hillshade on the elevation data. This module bridges the
 * `TerrainToolCardState.hillshade` toggle to the Map's lighting API.
 */

export interface HillshadeOptions {
  azimuth?: number;
  zenith?: number;
  intensity?: number;
}

const DEFAULT_HILLSHADE: Required<HillshadeOptions> = {
  azimuth: 315,
  zenith: 45,
  intensity: 1.0,
};

/**
 * Apply GPU hillshade to a Giro3D Map entity.
 *
 * The Map's `lighting` property controls per-tile hillshade rendering
 * using the ElevationLayer's height data. When enabled, every map tile
 * receives a sun-illuminated shading computed in the fragment shader.
 */
export function applyHillshade(
  map: unknown,
  options: HillshadeOptions = {},
): void {
  const m = map as any;
  if (!m || typeof m !== 'object') return;

  const opts = { ...DEFAULT_HILLSHADE, ...options };

  // Giro3D Map.lighting = { mode, azimuth, zenith, intensity, zFactor }
  // mode values: 0 = None, 1 = Hillshade
  if ('lighting' in m) {
    m.lighting = {
      mode: 1, // MapLightingMode.Hillshade
      azimuth: opts.azimuth,
      zenith: opts.zenith,
      intensity: opts.intensity,
      zFactor: m.lighting?.zFactor ?? 1.0,
    };
  } else if (typeof m.setLighting === 'function') {
    m.setLighting({
      mode: 1,
      azimuth: opts.azimuth,
      zenith: opts.zenith,
      intensity: opts.intensity,
    });
  }
}

/**
 * Remove hillshade from a Giro3D Map entity — set lighting mode to None.
 */
export function removeHillshade(map: unknown): void {
  const m = map as any;
  if (!m || typeof m !== 'object') return;

  if ('lighting' in m) {
    m.lighting = {
      ...m.lighting,
      mode: 0, // MapLightingMode.None
    };
  } else if (typeof m.setLighting === 'function') {
    m.setLighting({ mode: 0 });
  }
}

/**
 * Toggle contour lines on a Giro3D Map entity.
 *
 * Giro3D renders GPU contour overlays from the Map's ElevationLayer height via
 * `map.contourLines` (a `ContourLineOptions`). These are draped on the actual 3D
 * relief — the clean, readily-available 3D-engine contour path.
 *
 * BUG-FIX (gap G20): Giro3D's `getContourLineOptions(input)` defaults
 * `enabled: input.enabled ?? false`, so passing `{ interval, color, opacity }`
 * WITHOUT `enabled` left contours permanently OFF — the toggle did nothing. We
 * now pass `enabled: true` explicitly (and `{ enabled: false }` to disable,
 * rather than `undefined`). Contours have no effect unless the Map has an
 * ElevationLayer, which is only mounted when the elevation-mesh flag is ON;
 * on the flat (flag-OFF) path there is no height, so contours stay invisible.
 */
export function setContourLines(
  map: unknown,
  enabled: boolean,
  interval: number = 10,
  color: string = '#000000',
  opacity: number = 0.6,
): void {
  const m = map as any;
  if (!m || typeof m !== 'object') return;

  if (enabled) {
    m.contourLines = {
      enabled: true,
      interval,
      // Secondary (thin) lines at 1/5 of the main interval, matching common DEM
      // contour cartography; keeps Giro3D from defaulting them to 100 m.
      secondaryInterval: Math.max(1, interval / 5),
      color,
      opacity,
      thickness: 1,
    };
  } else {
    m.contourLines = { enabled: false };
  }
}

/**
 * Apply vertical exaggeration to the Giro3D terrain (gap G20).
 *
 * IMPORTANT — why this scales the *Map entity's* `object3d` and NOT
 * `instance.scene.scale`:
 *
 *   Giro3D constructs its `Instance` with `scene.matrixWorldAutoUpdate = false`
 *   (see `@giro3d/giro3d/core/Instance.js`). The renderer therefore never
 *   recomputes the scene's world matrix from `scene.scale`, so the previous
 *   `instance.scene.scale.set(1,1,factor)` was effectively a no-op — the slider
 *   value never reached the rendered mesh. The Map entity, by contrast, owns the
 *   real terrain transform node: its `object3d` is the parent of every root
 *   `TileMesh` (`object3d.add(tile)`), and Giro3D explicitly force-updates that
 *   subtree's world matrices each frame. Scaling `object3d.scale.z` (and forcing
 *   the world-matrix recompute) is the mechanism Giro3D actually honours, so the
 *   stretch reaches the displaced tile geometry.
 *
 * Scaling is scoped to the terrain Map — not the whole scene — so 3D Tiles, BIM
 * and point-cloud layers are never distorted.
 *
 * BASE-ANCHORED Z (gap G20 fix): a plain `object3d.scale.setZ(factor)` scales
 * about world z=0, which only looks right when the terrain's true min elevation
 * is exactly 0. For a DEM whose floor is not 0, the whole surface skews and
 * lifts (low areas rise, the back wall shoots up) — obvious under the oblique
 * view. To stretch the relief IN PLACE about its true base we apply
 *   newZ = base + (z - base) * factor
 * which a `scale.z = factor` plus a compensating `position.z = base*(1-factor)`
 * reproduces exactly. At `factor === 1` the compensation is `base*0 = 0`, an
 * exact identity. `base` is the ElevationLayer's min elevation (passed by the
 * adapter); it defaults to 0 so the flag-OFF path stays a pure identity.
 *
 * The Map lighting `zFactor` is kept in sync so the GPU hillshade matches the
 * exaggerated geometry.
 *
 * The control is disabled when the elevation-mesh flag is OFF (flat ColorLayer
 * has zero height), so on that path this is only ever called with `factor === 1`
 * (an identity transform).
 */
export function setVerticalExaggeration(
  instance: unknown,
  map: unknown,
  factor: number,
  base: number = 0,
): void {
  const inst = instance as any;
  const m = map as any;
  const object3d = m?.object3d;

  // Anchor the stretch about the DEM's true base elevation (default 0).
  const safeBase = Number.isFinite(base) ? base : 0;
  // `+ 0` normalises the IEEE `-0` (e.g. 0 * (1 - 5)) to `0` so identity checks
  // and serialisation stay clean; it leaves every non-zero value unchanged.
  const compensationZ = safeBase * (1 - factor) + 0;

  let applied = false;
  if (object3d?.scale && typeof object3d.scale.setZ === 'function') {
    object3d.scale.setZ(factor);
    // Compensate so the surface stretches about `base`, not world z=0.
    if (object3d.position && typeof object3d.position.setZ === 'function') {
      object3d.position.setZ(compensationZ);
    }
    // Force the world-matrix recompute down the terrain subtree so the scaled Z
    // reaches every TileMesh this frame (scene.matrixWorldAutoUpdate is false).
    object3d.updateMatrixWorld?.(true);
    applied = true;
    // Ops proof that the slider value reaches the real terrain object (not just
    // UI state, and not the dead scene-scale path).
    console.log(
      '[terrainLighting] vertical exaggeration applied: map.object3d.scale.z=', factor,
      'base=', safeBase, 'position.z=', compensationZ,
    );
  } else if (inst?.scene?.scale?.set) {
    // Fallback for environments/mocks that only expose a scene scale.
    inst.scene.scale.set(1, 1, factor);
    console.log('[terrainLighting] vertical exaggeration applied (fallback): scene.scale.z=', factor);
  }
  void applied;

  // Keep hillshade zFactor in sync so shading matches the exaggeration.
  if (m?.lighting) {
    m.lighting = {
      ...m.lighting,
      zFactor: factor,
    };
  }

  inst?.notifyChange?.();
}
